<script setup lang="ts">
import { VDateInput } from 'vuetify/labs/VDateInput'
import type MDBSemi from '@/types/ferme/semi'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, roundTo, stringArrayEqual, emptyMongoId, toDate_wd_dd_MMMM_hh_mm, getSemiSuperficie } from '@/utils/helpers'
import { unitésParBoîte, newVegcoBucket, getFournisseurLettresDappel } from '@/utils/helpers-vegco'
import DialogHistorique from '../dialogs/DialogHistorique.vue'
import type MDBVegcoCommandeVente from '@/types/vegco/commandevente'

const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const dialog = ref(false)
const modifications = ref(false)
const isEdit = ref(false)
const peuxPublier = computed(() => !storeU.utilisateur.accèsNon.includes('site.VGCO.put.vgco.buckets.publish'))
const balisesChoix = ref([] as string[])
const bucketSource = ref({} as MDBVegcoBucket)
const bucket = ref(newVegcoBucket())
const semiBuckets = ref([] as MDBVegcoBucket[])
const hasChildBuckets = ref(false)
const childBucketsQté = ref(0)
const unitParBoîte = computed(() => unitésParBoîte(bucket.value.culture))
const qté = ref(0)
const qtéConfirmée = computed(() => roundTo(bucket.value.quantitéConfirmée / unitParBoîte.value, 0))
const qtéPrévision = ref(0)
const qtéUnité = ref(0)
const qtéConfirméeUnité = ref(0)
const qtéParPalette = ref(0)
const isPublished = computed(() => { return new Date(bucket.value.datePublication).getFullYear() > 2000 })
const isCommandeVente = computed(() => { return bucket.value.commandeVenteId != emptyMongoId() })
const isInventory = computed(() => { return bucket.value.semiId == emptyMongoId() && bucket.value.commandeVenteId == emptyMongoId() })
const isSemiBucket = computed(() => { return bucket.value.semiId != emptyMongoId() && bucket.value.commandeVenteId == emptyMongoId() })
const comesFromInventory = computed(() => { return bucket.value.semiId == emptyMongoId() })
const datePublié = computed(() => toDate_wd_dd_MMMM_hh_mm(bucket.value.datePublication))
const ctItemsParFournisseur = computed(() => {
  const groupMap = new Map<string, { nbPalettes: number; quantitéTotale: number; items: typeof bucket.value.items; noCommandesAchat: Set<string> }>()
  for (const item of bucket.value.items) {
    const key = item.fournisseurId || ''
    if (!groupMap.has(key)) {
      groupMap.set(key, { nbPalettes: 0, quantitéTotale: 0, items: [], noCommandesAchat: new Set() })
    }
    const g = groupMap.get(key)!
    g.nbPalettes += 1
    g.quantitéTotale += item.quantité
    g.items.push(item)
    if (item.noCommandeAchat) g.noCommandesAchat.add(item.noCommandeAchat)
    console.log('[DialogBucket] poids item', {
      paletteId: item.paletteId,
      quantité: item.quantité,
      poidsBrut: item.poidsBrut,
      poidsContenant: item.poidsContenant,
      poidsPalette: item.poidsPalette,
      poidsNet: item.poidsBrut - (item.poidsContenant * item.quantité) - item.poidsPalette
    })
  }
  return Array.from(groupMap.entries())
    .map(([fournisseurId, g]) => ({
      fournisseurId,
      nbPalettes: g.nbPalettes,
      quantitéTotale: roundTo(g.quantitéTotale / unitParBoîte.value, 1),
      quantitéItemsTotal: g.items.reduce((s, i) => s + i.quantité, 0),
      items: g.items,
      noCommandesAchat: Array.from(g.noCommandesAchat).sort()
    }))
    .sort((a, b) => a.fournisseurId.localeCompare(b.fournisseurId))
})
const commande = ref(undefined as MDBVegcoCommandeVente | undefined)
const commandeDateChargé = computed(() => {
  if (commande.value == undefined) {
    return ''
  }
  return toDate_wd_dd_MMMM_hh_mm(commande.value.dateChargé)
})
const commandeIsLoading = ref(false)

let resolve: (value: boolean) => void
watch(dialog, (v) => {
  if (!v) {
    resolve(modifications.value)
  }
})

async function openBucketEdit(bucketEdit: MDBVegcoBucket, currentDate: Date | undefined) {
  console.log('[DialogBucket] raw bucket JSON', JSON.stringify(bucketEdit, null, 2))
  isEdit.value = true
  bucketSource.value = { ... bucketEdit }
  bucket.value = { ... bucketEdit }
  bucket.value.balises = [ ... bucketEdit.balises ]
  dialog.value = true
  qté.value = roundTo(bucket.value.quantité / unitParBoîte.value, 0)
  qtéPrévision.value = 0
  qtéUnité.value = 0
  qtéConfirméeUnité.value = 0
  hasChildBuckets.value = false
  childBucketsQté.value = 0

  await storeU.fetchBalisesIfNeeded()

  balisesChoix.value = storeU.getBalises('Bucket', 'Culture', bucket.value.culture,
    'Variété', bucket.value.variété).map(b => b.nom)

  if (isInventory.value && currentDate != undefined) {
    qtéPrévision.value = roundTo(
      storeVegco.getQuantitéPrévision(bucket.value.codeProduit, bucket.value.quantitéParPalette, currentDate) / unitParBoîte.value, 0)
  }

  if (bucket.value.semiId != emptyMongoId()) {
    try {
      const result = await apiProphet.get(`vgco/sites/VGCO/semis/${bucket.value.semiId}/buckets`)
      semiBuckets.value =  result.data as MDBVegcoBucket[]
      const childBuckets = semiBuckets.value.filter(b => b.bucketParentId == bucket.value.id)
      hasChildBuckets.value = childBuckets.length > 0

      // Take future buckets into account
      childBuckets.filter(b => b.date >= bucket.value.date).forEach(b => {
        childBucketsQté.value += b.quantité
      })
      childBucketsQté.value = roundTo(childBucketsQté.value / unitParBoîte.value, 0)
    }
    catch (e) {
      Notify.error(e)
      dialog.value = false
    }
  }

  if (bucket.value.codeProduit != '') {
    const produit = storeU.getVegcoProduit(bucket.value.codeProduit)
    if (produit != undefined) {
      qtéUnité.value = bucket.value.quantité / produit.quantité
      qtéConfirméeUnité.value = bucket.value.quantitéConfirmée / produit.quantité
      qtéParPalette.value = bucket.value.quantitéParPalette / produit.quantité
    }
  }

  commandeIsLoading.value = true
  if (bucket.value.commandeVenteId != emptyMongoId()) {
    commande.value = storeVegco.commandesStore.find(c => c.id == bucket.value.commandeVenteId)
    if (commande.value == undefined) {
      const result = await apiProphet.get(`vgco/commandes/vente/${bucket.value.commandeVenteId}`)
      commande.value = result.data as MDBVegcoCommandeVente
    }
  }
  commandeIsLoading.value = false

  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

async function retirerQtéManuelle() {
  if (bucket.value.semiId != emptyMongoId()) {
    try {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const result = await apiProphet.get(`sites/${siteCS}/semis/${bucket.value.semiId}`)
      const semi = result.data as MDBSemi
      const qtéSemi = roundTo(getSemiSuperficie(semi) * semi.rendementPrévu, 0)
      const otherMasterBucket = semiBuckets.value.find(b => b.id != bucket.value.id && b.bucketParentId == emptyMongoId())
      if (otherMasterBucket != undefined) {
        qté.value = (qtéSemi - otherMasterBucket.quantité)
      } else {
        qté.value = qtéSemi
      }
      bucketSource.value.quantité = qté.value
      qté.value = roundTo(qté.value / unitParBoîte.value, 0)
    } catch (e) {
      notifyError(e)
      return
    }
  }

  bucket.value.quantitéChangéeManuellement = false
}

function aUneModification() {
  if (
    bucket.value.date != bucketSource.value.date ||
    bucket.value.localisation != bucketSource.value.localisation ||
    bucket.value.priorité != bucketSource.value.priorité ||
    bucket.value.quantité != bucketSource.value.quantité ||
    bucket.value.balises.length != bucketSource.value.balises.length ||
    bucket.value.quantitéChangéeManuellement != bucketSource.value.quantitéChangéeManuellement ||
    bucket.value.noCommandeAchat != bucketSource.value.noCommandeAchat ||
    bucket.value.commentaires != bucketSource.value.commentaires) {
    return true
  }
  if (!stringArrayEqual(bucket.value.balises, bucketSource.value.balises)) {
    return true
  }
  return false
}

async function confirm() {
  try
  {
    if (isInventory.value) {
      dialog.value = false
      return
    }

    if (isEdit.value) {
        bucket.value.quantité = qté.value * unitParBoîte.value
        if (Math.abs(bucket.value.quantité - bucketSource.value.quantité) < unitParBoîte.value) {
          bucket.value.quantité = bucketSource.value.quantité
        }

        if (!aUneModification()) {
          dialog.value = false
          return
        }

        if (bucket.value.quantité != bucketSource.value.quantité) {
          bucket.value.quantitéChangéeManuellement = true
        }

        if (bucket.value.date != bucketSource.value.date) {
          bucket.value.dateChangéeManuellement = true
        }

        const siteCS = storeU.siteSélectionné.lettresDappel == 'VGCO' ? 'VGCO' :
          storeU.getSiteLettresDappel(bucket.value.siteId)

        await apiProphet.put(`vgco/sites/${siteCS}/buckets/${bucket.value.id}`, bucket.value)
        Notify.success('Modification du bucket avec succès');
        modifications.value = true
        dialog.value = false
    }
    else {
      // Create bucket here
    }
  }
  catch (e) {
    notifyError(e)
  }
}

function cancel() {
  dialog.value = false
}

const dialogHistorique = useTemplateRef('dialogHistorique')
function openHistorique() {
  dialogHistorique.value?.openHistBucketAsync(bucket.value)
}

async function publierCall(publier: boolean) {
  try {
    const config = { params: { publier: publier } }
    const result = await apiProphet.put(`vgco/buckets/${bucket.value.id}/publier`, null, config)
    bucket.value.datePublication = new Date(result.data).toISOString()
    modifications.value = true
  }
  catch (e) {
    notifyError(e)
  }
}

async function publier() {
  await publierCall(true)
}

async function dépublier() {
  await publierCall(false)
}

defineExpose({ openBucketEdit })
</script>

<template>
  <v-dialog v-model="dialog" max-width="900px" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span v-if="isInventory" class="text-h5 text-medium-emphasis ps-2">Bucket inventaire</span>
        <span v-else-if="isEdit" class="text-h5 text-medium-emphasis ps-2">Modifier un bucket</span>
        <span v-else class="text-h5 text-medium-emphasis ps-2">Nouveau bucket</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancel"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-text-field v-model="bucket.localisation" label="Localisation" readonly density="compact" variant="outlined"/>
            <v-text-field v-if="!comesFromInventory" v-model="bucket.numéroSemi" label="Numéro de semi" readonly density="compact" variant="outlined"/>
            <v-text-field v-model="bucket.culture" label="Culture" readonly density="compact" variant="outlined"/>
            <v-text-field v-if="bucket.variété != ''" v-model="bucket.variété" label="Variété" readonly density="compact" variant="outlined"/>
            <v-text-field v-if="bucket.codeProduit != ''" v-model="bucket.codeProduit" label="Code de produit" readonly density="compact" variant="outlined"/>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <span v-if="bucket.commandeVenteId != emptyMongoId()">
              <v-text-field
                v-if="commande" v-model="commandeDateChargé" :loading="commandeIsLoading"
                label="Date de chargement" readonly density="compact" variant="outlined"/>
              <v-text-field
                v-else model-value="Chargement" :loading="commandeIsLoading"
                label="Date de chargement" readonly density="compact" variant="outlined"/>
            </span>
            <span>
              <v-date-input
                v-if="isInventory" v-model="bucket.date" label="Date de la plus vielle palette"
                display-format="normalDateWithWeekday" readonly density="compact" variant="outlined" prepend-icon=""/>
              <v-date-input
                v-else v-model="bucket.date" label="Date récolte"
                display-format="normalDateWithWeekday" prepend-icon=""/>
            </span>
            <span v-if="isCommandeVente">
              <v-text-field
                v-if="isPublished" v-model="datePublié" label="Date publication"
                readonly density="compact" variant="outlined"/>
              <v-text-field
                v-else label="Date publication" model-value="Non publié"
                readonly density="compact" variant="outlined"/>
            </span>
            <span>
              <v-date-input
                v-if="isInventory"
                v-model="bucket.valideDu" label="Dernière modification" display-format="normalDateWithWeekday"
                readonly density="compact" variant="outlined" prepend-icon=""/>
            </span>
            <span v-if="bucket.dateChangéeManuellement">
              La date du bucket a été changée manuellement
            </span>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <span v-if="isInventory">
              <v-text-field v-model="qté" label="Quantité disponible en inventaire" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="qtéUnité != 0" v-model="qtéUnité" label="Unités disponibles en inventaire" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="qtéPrévision > 0" v-model="qtéPrévision" label="Quantité en prévision" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="qtéConfirmée" label="Quantité réelle en inventaire" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="qtéConfirméeUnité != 0" v-model="qtéConfirméeUnité" label="Unités réelles en inventaire" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="qtéParPalette" label="Quantité par palette" type="number" readonly density="compact" variant="outlined"/>
            </span>
            <span v-else>
              <v-text-field v-if="isSemiBucket" v-model="qté" label="Quantité" type="number"/>
              <v-text-field v-else v-model="qté" label="Quantité" type="number" readonly density="compact" variant="outlined"/>
              <div v-if="childBucketsQté > 0" class="mb-3">
                Il y a {{ childBucketsQté }} boîtes qui sont attribuées à des commandes futures.
              </div>
              <v-text-field v-if="qtéUnité != 0" v-model="qtéUnité" label="Unités" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="qtéConfirmée != 0" v-model="qtéConfirmée" label="Quantité confirmée" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="qtéConfirméeUnité != 0" v-model="qtéConfirméeUnité" label="Unités confirmées" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="qtéParPalette" label="Quantité par palette" type="number" readonly density="compact" variant="outlined"/>
            </span>

            <div v-if="bucket.quantitéChangéeManuellement">
              <v-btn v-if="bucket.siteId == storeU.siteSélectionné.id" class="mb-2" color="secondary" @click="retirerQtéManuelle()">Retirer la quantité manuelle</v-btn><br/>
              <span>
                La quantité du bucket a été changée manuellement
              </span>
            </div>
            <span v-if="bucket.semiFermé">
              Le semi est fermé
            </span>
          </v-col>
        </v-row>

        <v-row v-if="!comesFromInventory" class="mt-n2">
          <v-col>
            <v-text-field v-model="bucket.priorité" label="Priorité" type="number" required/>
          </v-col>
          <v-col>
            <span v-if="!isInventory">
              <v-select
                v-if="isCommandeVente || hasChildBuckets" v-model="bucket.balises"
                label="Balises" chips multiple readonly variant="outlined"/>
              <v-select
                v-else v-model="bucket.balises"
                :items="balisesChoix" label="Balises" chips multiple/>
              <span v-if="hasChildBuckets" class="text-body-2">Le bucket a des sous-buckets attritrés à des commandes.</span>
            </span>
          </v-col>
        </v-row>

        <v-row class="mt-n2">
          <v-col cols="6">
            <v-text-field
              v-if="!comesFromInventory"
              v-model="bucket.noCommandeAchat" label="No commande d'achat" type="number" required/>
          </v-col>
          <v-col v-if="commande" cols="6">
            <v-text-field v-model="commande.noCommande" label="No commande de vente" type="number" readonly density="compact" variant="outlined"/>
          </v-col>
        </v-row>

        <v-row v-if="isInventory && bucket.items.length > 0" class="mt-0">
          <v-col cols="12">
            <div class="text-subtitle-2 mb-2">Détail par commande d'achat</div>
            <v-expansion-panels variant="accordion">
              <v-expansion-panel v-for="g in ctItemsParFournisseur" :key="g.fournisseurId">
                <v-expansion-panel-title>
                  <div class="d-flex align-center justify-space-between w-100 pr-4">
                    <span class="font-weight-medium">{{ getFournisseurLettresDappel(g.fournisseurId) || '(fournisseur inconnu)' }}</span>
                    <span v-if="g.noCommandesAchat.length > 0" class="text-medium-emphasis text-body-2 ml-3">
                      {{ g.noCommandesAchat.join(', ') }}
                    </span>
                    <div class="d-flex" style="gap: 16px;">
                      <span class="text-medium-emphasis text-body-2">{{ g.nbPalettes }} palette{{ g.nbPalettes > 1 ? 's' : '' }}</span>
                      <span class="text-body-2">{{ g.quantitéItemsTotal }} boîtes</span>
                    </div>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-table density="compact">
                    <thead>
                      <tr>
                        <th>Palette ID</th>
                        <th>No cmd achat</th>
                        <th>Date réception</th>
                        <th>Date récolte</th>
                        <th class="text-right">Quantité (boîtes)</th>
                        <th class="text-right">Poids brut (lbs)</th>
                        <th class="text-right">Poids net (lbs)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in g.items" :key="item.paletteId">
                        <td>{{ item.paletteId }}</td>
                        <td>{{ item.noCommandeAchat }}</td>
                        <td>{{ toDate_wd_dd_MMMM_hh_mm(item.dateRéception) }}</td>
                        <td>
                          <span v-if="new Date(item.dateRécolte).getFullYear() === new Date(item.dateRéception).getFullYear()">{{ toDate_wd_dd_MMMM_hh_mm(item.dateRécolte) }}</span>
                          <v-chip v-else size="small" color="warning">information manquante</v-chip>
                        </td>
                        <td class="text-right">{{ item.quantité }}</td>
                        <td class="text-right">
                          <v-chip v-if="item.poidsBrut === 0" size="small" color="grey">non pesé</v-chip>
                          <span v-else>{{ item.poidsBrut }}</span>
                        </td>
                        <td class="text-right">
                          <span v-if="item.poidsBrut !== 0">{{ roundTo(item.poidsBrut - (item.poidsContenant * item.quantité) - item.poidsPalette, 1) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <!--<v-row class="mt-n8">
          <v-col>
            <v-text-field v-model="récolte.quantitéUnité" label="Unité" readonly variant="outlined"/>
          </v-col>
          <v-col v-if="!isEdit">
            <v-text-field v-model="priorité" label="Priorité" type="number" required/>
          </v-col>
        </v-row>-->

        <v-textarea
          v-if="!isInventory || bucket.commandeVenteId != emptyMongoId()"
          v-model="bucket.commentaires" rows="2" label="Commentaires" required/>

        <div v-if="isEdit">
          <v-btn v-if="!isInventory" color="secondary" variant="flat" @click="openHistorique">Voir historique</v-btn>
          <span v-if="peuxPublier && isCommandeVente" class="ml-2">
            <v-btn v-if="isPublished" color="secondary" variant="flat" @click="dépublier">Dé-publier</v-btn>
            <v-btn v-else color="secondary" variant="flat" @click="publier">Publier</v-btn>
          </span>

        </div>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="d-flex justify-end mr-2 my-2">
        <v-btn @click="cancel">Annuler</v-btn>
        <span v-if="!isInventory">
          <v-btn v-if="isEdit" color="primary" variant="flat" @click="confirm">Modifier le bucket</v-btn>
          <v-btn v-else color="primary" variant="flat" @click="confirm">Créer un nouveau bucket</v-btn>
        </span>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <DialogHistorique ref="dialogHistorique"/>
</template>
